import { TabInfo, TabType, PagesState, PopupInfo, tabFocusSelf } from '../reducers/pages';
import {
  createPageID,
  IntegratedDrawingState,
  collectPictureIDsFromIntegratedDrawingState,
  JSONReadyIntegratedDrawingData,
  collectPictureIDsFromJSONReadyIntegratedDrawingData,
} from '../reducers/integratedDrawing';
import { collectPictureIDsFromDrawingState } from '../drawing/reducers/drawing';
import { MultiroleLinkInfo } from '../../../../common/src/ts/types/multiroleLink';
import { ShapeKind } from '../drawing/types';
import { Publication } from '../types/epub';
import { getDefaultRendition, buildSpreadBlock } from '../utils/epub';

function getEditorKeyAndPageForTab(
  tab: TabInfo,
  publication: Publication,
): [string, number | null] {
  switch (tab.type) {
    case TabType.LinearPages: {
      const { left, right } = buildSpreadBlock(getDefaultRendition(publication), tab.currentPage);
      if (left && right) {
        return ['PRIMARY', Math.min(left.linearIndex, right.linearIndex)];
      }
      return ['PRIMARY', tab.currentPage];
    }
    case TabType.ExtraPage:
      return [tab.path, null];
    case TabType.GeneratedContent:
      return [`ark-generated#${tab.key}`, null];
    case TabType.ReflowablePages:
      return ['', null]; // Drawing is not supported.
  }
}

function getEditorKeyAndPageForPopup(popup: PopupInfo): [string, null] {
  return [popup.path, null];
}

export function getEditorKeyAndPage(
  pages: PagesState,
  publication: Publication,
): [string, number | null] {
  const tab = pages.tabs[pages.activeTab];
  if (tab.focus === tabFocusSelf) {
    return getEditorKeyAndPageForTab(tab, publication);
  }

  if (tab.focus in pages.popups) {
    return getEditorKeyAndPageForPopup(pages.popups[tab.focus]);
  }

  throw new Error('Focused popup is not found.');
}

export function createPageIDForTab(tab: TabInfo, publication: Publication): string {
  const [key, page] = getEditorKeyAndPageForTab(tab, publication);
  return createPageID(key, page);
}

export function createPageIDForPopup(popup: PopupInfo): string {
  const [key, page] = getEditorKeyAndPageForPopup(popup);
  return createPageID(key, page);
}

export function createIsPictureUsed(drawing: IntegratedDrawingState) {
  const ids: { [id: string]: true } = {};
  for (const id of collectPictureIDsFromIntegratedDrawingState(drawing)) {
    ids[id] = true;
  }

  return (id: string) => id in ids;
}

export function createIsPictureToBeSaved(drawing: JSONReadyIntegratedDrawingData) {
  const ids: { [id: string]: true } = {};
  for (const id of collectPictureIDsFromJSONReadyIntegratedDrawingData(drawing)) {
    ids[id] = true;
  }

  return (id: string) => id in ids;
}

export function createIsImageDemanded(
  drawing: IntegratedDrawingState,
  pages: PagesState,
  publication: Publication,
) {
  const ids: { [id: string]: true } = {};

  const editorkeys = pages.tabs.map(
    (tab) => { return getEditorKeyAndPageForTab(tab, publication); },
  );

  editorkeys.concat(
    Object.keys(pages.popups).map((value) =>
      { return getEditorKeyAndPageForPopup(pages.popups[value]); },
  ));

  editorkeys.forEach((editorkey) => {
    const [key, page] = editorkey;

    const pageID = createPageID(key, page);
    if (pageID in drawing.currentData) {
      for (const id of collectPictureIDsFromDrawingState(drawing.currentData[pageID])) {
        ids[id] = true;
      }
    }

    /**
     * FIXME : It has to be unnecessary since drawable images of pictures which are not to be saved
     *         are kept alive, but somehow flicker appears at redoing right now.
     */
    if (key in drawing.histories) {
      const history = drawing.histories[key];

      for (let index = history.pastRecords.length - 1; index >= 0; index -= 1) {
        const record = history.pastRecords[index];
        if (record.page !== page) {
          break;
        }

        for (const id of collectPictureIDsFromDrawingState(record.data)) {
          ids[id] = true;
        }
      }

      for (let index = history.futureRecords.length - 1; index >= 0; index -= 1) {
        const record = history.futureRecords[index];
        if (record.page !== page) {
          break;
        }

        for (const id of collectPictureIDsFromDrawingState(record.data)) {
          ids[id] = true;
        }
      }
    }
  });

  return (id: string) => id in ids;
}

export function getMultiroleLinks(
  state: IntegratedDrawingState,
): MultiroleLinkInfo[] {
  const multiroleLinks: MultiroleLinkInfo[] = [];
  for (const key in state.currentData) {
    const { layers } = state.currentData[key];
    for (const layerID in layers) {
      const { shape } = layers[layerID];
      if (shape.kind === ShapeKind.MultiroleLink) {
        multiroleLinks.push(shape.multiroleLink);
      }
    }
  }
  return multiroleLinks;
}
